<template>
  <div>
    <vx-card class="bg-primary text-white">
      <h1 class="text-white"> Encuentra tu distrito o municipio ideal para vivir en España</h1>
      <p class="mt-2"> Aqui podras consultar toda la información que necesitas para que puedas decidir si un distrito o municipio es adecuado para ti y para los tuyos. </p>
      <div class="flex gap-4 mt-8">
        <v-select
          class="bg-white rounded w-full" 
          placeholder="Escribe una ubicación donde buscar"
          :closeOnSelect="true"
          v-model="location"
          :options="listOfLocations"
          :filter="filter"
        >
          <template #option="{ barrio, distrito, municipio, provincia }">
            <p>
              <vs-list>
                {{ municipio !== provincia ? `${provincia}, ${municipio}, ${distrito}` : `${municipio}, ${distrito}` }}
                {{ barrio ? `, ${barrio}` : '' }}
              </vs-list>
            </p>
          </template>
          <template #selected-option="{ barrio, distrito, municipio, provincia }">
            <p>
              <vs-list>
                {{ municipio !== provincia ? `${provincia}, ${municipio}, ${distrito}` : `${municipio}, ${distrito}` }}
                {{ barrio ? `, ${barrio}` : '' }}
              </vs-list>
            </p>
          </template>
        </v-select>
      </div>
    </vx-card>
    <br>
    
    <vx-card v-if="location !== null">
      <div class="flex flex-col gap-16">
        <div class="flex flex-col md:flex-row gap-8 mb-8">
          <div class="w-full flex flex-col gap-8">
            <div>
              <h1>Vivir en {{location.distrito ? location.distrito + ',' : '' }} {{location.municipio}}</h1>
              <p>
                Tanto si quieres comprar, alquilar o invertir en un inmueble, es importante conocer en profundidad el distrito para saber si es adecuado para ti.
              </p>
              <p>  
                El distrito de {{location.distrito}} cuenta con
                <span v-if="location.listingsForSell && location.listingsForRent">
                  mas de <b>{{location.listingsForSell}} inmuebles en venta</b> y mas de <b>{{location.listingsForRent}} inmuebles en alquiler</b>.
                </span>
                <span v-else> poca información acerca del numero de inmuebles en venta y en alquiler </span>
              </p>
            </div>
            <div class="flex flex-col gap-4" v-if="location.listingsSell && location.listingsRent">
              <h3>Precio medio para un inmueble de 80 m²</h3>
              <div class="flex gap-8">
                <div class="flex flex-col gap-4" v-if="location.listingsSell != 0">
                  Precio medio de venta: <br>  
                  <span class="flex gap-4 items-center h3 text-bold">
                    <feather-icon icon="HomeIcon"/> {{location.listingsSell | formatoMiles }} €
                  </span>
                </div>

                <div class="flex flex-col gap-4" v-if="location.listingsRent != 0">
                  Precio medio de alquiler: <br>
                  <span class="flex gap-4 items-center h3 text-bold">
                    <feather-icon icon="HomeIcon"/> {{location.listingsRent | formatoMiles }} €
                  </span>
                </div>
              </div>
            </div>
          </div>
          <div class="w-full flex flex-col gap-4 items-center">
            <img :src="`https://maps.googleapis.com/maps/api/staticmap?center=${mapCodify(location)}&size=500x300&key=AIzaSyC6gZD3hkDmiDsTJ-xYYelV9_pKXupBXAs`"
              class="w-full rounded" :alt="`${location.provincia}+${location.municipio}+${location.distrito}`">
              <div v-if="location.streetviewurl">
              <a :href="location.streetviewurl">
                <vs-button icon-pack="feather" icon="icon-compass"> Ver en Street View</vs-button>
              </a>
            </div>
          </div>
        </div>
      
        <div class="flex flex-col gap-8 mb-8" v-if="location.services && location.services.length">
          <hr><br><h1> Servicios de {{location.distrito ? location.distrito + ',' : '' }} {{location.municipio}} </h1>

          <div class="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div v-for="i in location.services.filter(i=>i.label)" :key="i.label" class="flex flex-col gap-4 border-2 border-solid rounded d-theme-border-grey-light p-4">
              <h3 class="flex gap-4 items-center">
                <i v-if="i.label === 'salud'"         class="material-icons"> local_hospital </i>
                <i v-if="i.label === 'educacion'"     class="material-icons"> school </i>
                <i v-if="i.label === 'transporte'"    class="material-icons"> directions_transit </i>
                <i v-if="i.label === 'supermercado'"  class="material-icons"> shopping_cart </i>
                {{ i.label | capitalize }}
              </h3>
              <p>{{ i.value | capitalize }}</p>
            </div>
          </div>
        </div>
      
        <div class="flex flex-col gap-8 mb-8"  v-if="location.ratings && location.ratings.length && location.ratings.filter(i=>i.value !== 0).length !== 0">
          <hr><br><h1> Que opinan los residentes de {{location.distrito ? location.distrito + ',' : '' }} {{location.municipio}}</h1>
          <div class="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div v-for="i in location.ratings.filter(i=>i.value !== 0)" :key="i.label" class="flex flex-col gap-4 py-4">
              <div class="flex gap-4 items-center">
                <div>
                  <i class="material-icons" v-if="i.label === 'transporte'">   directions_transit </i>
                  <i class="material-icons" v-if="i.label === 'iluminacion'">  brightness_high </i>
                  <i class="material-icons" v-if="i.label === 'tranquilidad'"> local_florist </i>
                  <i class="material-icons" v-if="i.label === 'restaurant'">   restaurant </i>
                  <i class="material-icons" v-if="i.label === 'parques'">      nature_people </i>
                  <i class="material-icons" v-if="i.label === 'hijos'">        child_friendly </i>
                  <i class="material-icons" v-if="i.label === 'pasear'">       beach_access </i>
                  <i class="material-icons" v-if="i.label === 'deporte'">      fitness_center </i>
                  <i class="material-icons" v-if="i.label === 'aparcamiento'"> local_parking </i>
                  <i class="material-icons" v-if="i.label === 'seguridad'">    security </i>
                  <i class="material-icons" v-if="i.label === 'perro'">        pets </i>
                </div>
                <div>
                  <span v-if="i.label === 'transporte'"> Transportes públicos </span>
                  <span v-if="i.label === 'iluminacion'"> Iluminación de las calles </span>
                  <span v-if="i.label === 'tranquilidad'"> Tranquilidad de la zona </span>
                  <span v-if="i.label === 'restaurant'"> Restaurantes </span>
                  <span v-if="i.label === 'parques'"> Plazas y parques </span>
                  <span v-if="i.label === 'hijos'"> Para vivir con hijos </span>
                  <span v-if="i.label === 'pasear'"> Para pasear </span>
                  <span v-if="i.label === 'deporte'"> Para hacer deporte </span>
                  <span v-if="i.label === 'aparcamiento'"> Facilidad de aparcamiento </span>
                  <span v-if="i.label === 'seguridad'"> Seguridad del distrito </span>
                  <span v-if="i.label === 'perro'"> Para vivir con perros </span>
                  <br>
                  <div>
                    <span v-for="j in 5" :key="j" class="text-warning text-xl">
                      <template v-if="j <= Math.floor(i.value)">
                        <span class="material-icons">star</span>
                      </template>
                      <template v-else-if="j === Math.ceil(i.value) && i.value % 1 >= 0.5">
                        <span class="material-icons">star_half</span>
                      </template>
                      <template v-else>
                        <span class="material-icons">star_outline</span>
                      </template>
                    </span>
                    <span class="pl-4">{{ i.value }}/5</span></div>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>
    </vx-card>
</div>
</template>
<script>
import axios from '@/axios.js'
import vSelect from 'vue-select'
export default {
  components: {
    'v-select': vSelect
  },
  mounted () {
    this.fetchLocations()
  },
  methods: {
    mapCodify (location) {
      return encodeURI(`${location.provincia}+${location.municipio}+${location.distrito}+${location.barrio}`)
    },
    fetchLocations () {

      if (location) {
        let url = new URL(location.href)
        
        url = `${url.host.search('localhost') !== -1 ? 'http://' : 'https://'}${url.host}${url.pathname}where-to-live-guide.json`

        axios({ method: 'get', url })
          .then(response => {
            console.log(response.data)
            this.listOfLocations = response.data.filter(i => i.CCAA)
            console.log(this.listOfLocations)
          })
          .catch(err => {
            console.error(err)
            const mensaje_error = 'Ha ocurrido un error'
            if (typeof mensaje_error === 'string') {
              this.$vs.notify({ title: 'Error', text: `${mensaje_error}`, color: 'warning', iconPack: 'feather', icon:'icon-x-circle', time: 6000 })
            } else {
              const array_errors = Object.entries(mensaje_error)
              for (let i = 0; i < array_errors.length; i++) {
                this.$vs.notify({ title: 'Error', text: `${array_errors[i][1][0]}`, iconPack: 'feather', icon: 'icon-x-circle', color: 'warning', time: 6000 })
              }
            }
          })
      }
    },
    filter (options, search) {
      const removeAccents = (text) => {
        const accentsMap = { 'á': 'a', 'é': 'e', 'í': 'i', 'ó': 'o', 'ú': 'u', 'à': 'a', 'è': 'e', 'ì': 'i', 'ò': 'o', 'ù': 'u' }
        return text.replace(/[áéíóúàèìòùÁÉÍÓÚÀÈÌÒÙ]/g, char => accentsMap[char] || char)
      }
      search = removeAccents(search.toLowerCase())

      let filtered = options.filter(item => {
        const searchWords = search.split(/\s+/)
        return searchWords.every(searchWord => {
          const values = Object.values(item).map(val => {
            if (typeof val === 'string') { 
              if (val) return removeAccents(val.toLowerCase()) 
            }
          })
          return values.some(val => {
            if (val) return val.includes(removeAccents(searchWord))
          })
        })
      })

      filtered = filtered.sort((a, b) => {
        a = a.provincia + a.municipio + a.distrito + a.barrio
        b = b.provincia + b.municipio + b.distrito + b.barrio
        return a.length - b.length
      })
      return filtered
    }
  },
  data () {
    return {
      search: '',
      location: null,
      listOfLocations : []
    }
  }
}
</script>
